/*======================================================================================================================
  * Bluebell: src/app/bramble/results/results.service.ts
  * Last Edited: 30/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, Injectable, signal, inject } from '@angular/core';
import { WAMPService } from '@bramble/wamp.service';
import { tapResponse } from '@ngrx/operators';
import {
  calculationParseComplete,
  retrieveCalculations,
  setCalculationLoading,
} from '@state/results/results.actions';
import {
  Result,
  BaseResultsParams,
  ProjectResultsParams,
  ListResultsResponse, Calculation, DeltaSelectionType, ForceGroups, DatedResultsParams,
} from '@state/results/results.interfaces';
import {
  selectCalculations, selectCalculationsTotal,
} from '@state/results/results.selectors';
import { ApiService } from '@state/api.service';
import {
  ResultsColumnConfig,
  ResultsView,
} from '@state/results-view/results-view.interfaces';
import {
  selectCurrentView,
} from '@state/results-view/results-view.selectors';
import { Store } from '@ngrx/store';
import { selectCurrentProject, selectCurrentProjectId } from '@state/projects/project.selectors';
import { Observable, of } from 'rxjs';
import { MessageService } from 'primeng/api';


@Injectable({
  providedIn: 'root',
})
export class ResultsService {
  private store = inject(Store);
  private api = inject(ApiService);
  private messageService = inject(MessageService);
  private wamp = inject(WAMPService);

  // PPR + User
  private selectedProjectId = this.store.selectSignal(selectCurrentProjectId);
  private selectedProject = this.store.selectSignal(selectCurrentProject);

  // Project Force Groups
  forceGroupsLoading = signal<boolean>(false);
  forceGroupsNew = signal<ForceGroups[]>([]);
  calculations = this.store.selectSignal(selectCalculations);
  calculationsCount = this.store.selectSignal(selectCalculationsTotal);
  parameters = computed(() => {
    if (this.selectedProjectId()) {
      const project = this.store.selectSignal(selectCurrentProject);
      return Object.keys(project()?.parameters || {});
    }

    return [];
  });
  mapParameters = computed(() => {
    if (this.selectedProjectId()) {
      const project = this.store.selectSignal(selectCurrentProject);
      return Object.keys(project()?.map_parameters || {});
    }

    return [];
  });

  // Table Setups
  private currentView = this.store.selectSignal(selectCurrentView);
  private calculationColumns = computed(() => this.currentView()?.columns.filter(col => col.calculation) || []);
  private deltaToLeadCase = computed(() => this.currentView().deltaToLeadCase);
  private deltaBaseline = computed(() => this.currentView().deltaBaseline);

  private wampSubscriptions: any[] = [];

  constructor() {
    effect(() => {
      if (this.selectedProject() && !this.calculationsCount()) {
        this.store.dispatch(retrieveCalculations());
      }
    });
    // WAMP subscription for tracking calculation result processing
    effect(() => {
      if (this.wamp.connected()) {
        const uuids: string[] = this.calculations().map(calc => calc.uuid);

        // Unsubscribe and remove sub if calculation is no longer present
        for (const sub of this.wampSubscriptions) {
          const subUuid: string = sub.topic.replace('bramble.publish.parse_calculation', '');
          if (!(uuids.includes(subUuid))) {
            sub.unsubscribe();
          }
        }
        this.wampSubscriptions = this.wampSubscriptions.filter(sub => {
          const subUuid: string = sub.topic.replace('bramble.publish.parse_calculation', '');
          return uuids.includes(subUuid);
        });

        // Establish the new calculation
        uuids.forEach(uuid => this.createCalculationUpdateWebsocket(uuid));
      } else {
        // Unsubscribe and remove all subscriptions
        for (const sub of this.wampSubscriptions) {
          sub.unsubscribe();
        }
        this.wampSubscriptions = [];
      }
    });
  }

  loadForceGroups(selections: string[]|undefined = undefined): void {
    this.forceGroupsLoading.set(true);
    if (!selections) {
      this.api.listForceGroups(this.selectedProjectId() as string, {}).pipe(
        tapResponse({
          next: (forceGroups: {data: ForceGroups[]}) => {
            this.forceGroupsNew.set(forceGroups.data);
            this.forceGroupsLoading.set(false);
          },
          error: (err: HttpErrorResponse) => {
            if (err.status === 404) {
              this.forceGroupsNew.set([]);
              this.forceGroupsLoading.set(false);
            }
          }
        })
      ).subscribe();
    } else {
      this.api.listForceGroups(this.selectedProjectId() as string, { selections }).pipe(
        tapResponse({
          next: (forceGroups: {data: ForceGroups[]}) => {
            forceGroups.data.forEach((fg: ForceGroups) => {
              this.forceGroupsNew.update((fGroups: ForceGroups[]) => {
                const idx = fGroups.findIndex(g => g.type === fg.type);
                fGroups.splice(idx, 1);
                fGroups.push(fg);
                return fGroups;
              });
            });
            this.forceGroupsLoading.set(false);
          },
          error: (err: HttpErrorResponse) => {
            if (err.status === 404) {
              this.forceGroupsLoading.set(false);
            }
          }
        })
      ).subscribe();
    }
  }

  /**
   * Establishes a WebSocket subscription for updating calculation results upon processing completion.
   *
   * @param {string} calcUuid - The unique identifier for the calculation used to set the WebSocket topic.
   */
  createCalculationUpdateWebsocket(calcUuid: string): void {
    // Check through existing wampSubscriptions to avoid duplicates
    for (const sub of this.wampSubscriptions) {
      const uuid: string = sub.topic.replace('bramble.publish.parse_calculation', '');
      if (uuid === calcUuid) {
        return;
      }
    }

    // Websocket subscription for reloading after processing calculation results
    const topic = `bramble.publish.parse_calculation${calcUuid}`;
    this.wamp.subscribe(topic, (args: any, kwargs: any) => {
      if (kwargs.event === 'start') {
        this.store.dispatch(setCalculationLoading({ uuid: calcUuid, loading: true }));
        this.messageService.add({
          severity: 'info',
          summary: 'Processing Calculation',
          detail: 'Calculation results may take a short time to process.',
        });
      } else if (kwargs.event === 'success') {
        this.store.dispatch(setCalculationLoading({ uuid: calcUuid, loading: false }));
        this.store.dispatch(calculationParseComplete());
      } else if (kwargs.event === 'error') {
        this.store.dispatch(setCalculationLoading({ uuid: calcUuid, loading: false }));
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'There was an error processing the calculation.',
        });
      }
    }).then((sub: any) => this.wampSubscriptions.push(sub));
  }

  // FORCE UTILS FUNCTIONS

  /**
   * Round the given number
   */
  parseNumber(value: string, opts: ResultsColumnConfig, deltaValue: boolean, deltaMode: DeltaSelectionType = 'absolute') {
    let currentScalingFactor = 1;
    if (opts.scalingFactor) {
      currentScalingFactor = opts.scalingFactor;
    }
    if (value === '-') {
      return '-';
    }
    let number = parseFloat(value);
    if (opts.negation) {
      number = -number;
    }
    if (!deltaValue) {
      if (opts.percentage) {
        number = 100 * number;
      } else {
        if (currentScalingFactor !== 1) {
          number = number * currentScalingFactor;
        }
      }
    } else {
      if (!deltaMode) {
        if (opts.percentage) {
          number = 100 * number;
        } else {
          if (!opts.percentageDelta) {
            number = number * currentScalingFactor;
          }
        }
      } else {
        if (opts.percentage) {
          number = 100 * number;
        } else {
          if (deltaMode === 'absolute') {
            number = number * currentScalingFactor;
          }
        }
      }
    }
    if (opts.roundingType === 'sf') {
      return opts.roundingDigits ? number.toPrecision(opts.roundingDigits) : number.toFixed(0);
    } else if (opts.roundingType === 'dp') {
      return number.toFixed(opts.roundingDigits);
    }
    return '-';
  }

  /**
   * Return the delta value of a piece of data in the correct format
   * @param data
   * @param opts
   * @param deltaMode
   */
  parseDeltas(data: any[], opts: ResultsColumnConfig, deltaMode: DeltaSelectionType) {
    let deltaRatio = 0;
    let deltaValue;

    let delta = '';
    if (opts.delta !== 'hide' || deltaMode) {
      if (deltaMode === 'percentage' || (!deltaMode && opts.percentageDelta)) {
        if (!opts.percentage) {
          deltaRatio = (
                         data[1] / Math.abs(data[0] - data[1])
                       ) * 100;
        } else {
          deltaRatio = data[1];
        }
        const deltaRatioString = `${deltaRatio}`;
        if (data[1] > -0.0005 && data[1] < 0.0005) {
          delta = '0.0%';
        } else {
          deltaValue = this.parseNumber(deltaRatioString, opts, true, deltaMode);
          delta = deltaValue + '%';
        }
      } else {
        deltaValue = data[1] !== 0 ? this.parseNumber(data[1], opts, true, deltaMode) : data[1];
        delta = deltaValue + (opts.percentage ? '%' : '');
      }
    }

    if (data[1] !== '-') {
      return delta;
    } else {
      return '';
    }
  }

  /**
   * Effects Functions
   */

  getCurrentResultsParams(): BaseResultsParams {
    const params: BaseResultsParams = {};
    const calculationColumns: string[] = this.calculationColumns().map(col => col.property);

    const calculations = new Set<string>();
    const monitors = new Set<string>();
    const probes = new Set<string>();
    const zones = new Set<string>();

    this.calculations().forEach(({ name, uuid }: Calculation) => {
      if (calculationColumns.includes(name)) {
        calculations.add(uuid);
      }
    });

    this.currentView().columns.forEach(({ calculation, property }: ResultsColumnConfig) => {
      if (!calculation) {
        if (property.includes('mean_zones')) {
          zones.add(property);
        } else if (property.includes('probe') || property.includes('pProbe')) {
          probes.add(property);
        } else {
          monitors.add(property);
        }
      }
    });

    if (calculations.size) {
      params.calculations = [...calculations];
    }

    if (monitors.size) {
      params.monitors = [...monitors];
    }

    if (probes.size) {
      params.probes = [...probes];
    }

    if (zones.size) {
      params.zones = [...zones].map(zone => zone.replace('mean_zones', 'mean'));
    }

    if (this.deltaBaseline()?.uuid) {
      params.delta_baseline_type = this.deltaBaseline()?.type;
      params.delta_baseline = this.deltaBaseline()?.uuid;
    }

    if (this.deltaToLeadCase() && this.selectedProjectId()) {
      params.delta_lead = true;
    }
    return params;
  }

  loadProjectResults(offset: number, limit: number): Observable<ListResultsResponse> {
    if (this.selectedProjectId()) {
      const params: ProjectResultsParams = {
        offset,
        limit,
        ...this.getCurrentResultsParams(),
      };

      return this.api.listProjectForces(this.selectedProjectId() as string, params);
    } else {
      return of({ results: [], all_runs_loaded: true });
    }

  }

  loadSandboxResults(uuid: string): Observable<Result[]> {
    return this.api.listSandboxForces(uuid, this.getCurrentResultsParams());
  }

  loadReportResults(uuid: string, view?: ResultsView): Observable<Result[]> {
    const params: BaseResultsParams = {};

    if (view) {
      const monitors: string[] = view.data.columns?.filter(col =>
                                   !col.calculation && !col.mapParameter && !col.parameter
                                 ).map(col => col.property) || [];

      const probes: string[] = view.data.columns?.filter(
        col => col.property.includes('probe') || col.property.includes('pProbe')
      ).map(col => col.property) || [];

      const calcs: string[] = view.data.columns?.filter(
        col => col.calculation
      ).map(col => col.property) || [];

      const baseline = view.data.deltaBaseline;

      // Only include params if not undefined, else can send confusing request to the backend
      if (monitors.length) {
        params.monitors = monitors;
      }

      if (calcs.length) {
        params.calculations = calcs.map(name => this.calculations().find(c => c.name === name)?.uuid ?? '')
      }

      if (probes.length) {
        params.probes = probes;
      }

      if (baseline?.uuid) {
        params.delta_baseline_type = baseline.type;
        params.delta_baseline = baseline.uuid;
      }

      if (view.data.deltaToLeadCase && this.selectedProjectId()) {
        params.delta_lead = true;
      }

    }

    return this.api.listReportForces(uuid, params);
  }

  loadResultsByDate(dateParams: {projects: string[], end_date: string, start_date: string}, uuid: string): Observable<Result[]> {
    const params: DatedResultsParams = {
      ...dateParams,
      ...this.getCurrentResultsParams(),
    };

    return this.api.listResultsByDate(params, uuid);
  }

  resultNameSort(a: string, b: string): number {
    const [aProject, aRun, ...aRest] = a.split('-');
    const [bProject, bRun, ...bRest] = b.split('-');
    const aRemainder = aRest.join('-');
    const bRemainder = bRest.join('-');
    const projectName = this.selectedProject()?.name;

    /**
     * Sort by the current project first (otherwise project reverse alphabetically)
     * then run reverse alphabetically, then Remainder reverse alphabetically
     *
     * if aProject IS the current project and bProject IS NOT, then a is first
     * if aProject iS NOT the current project and bProject IS then b is first
     * if both are in the current project then:
     *  if aRun IS NOT bRUN sort reverse alphabetically by Run
     *  else sort reverse alphabetically by the rest (map item)
     */

    const runAndMapSorting = (): number => {
      if (aRun === bRun) {
        // If a and b ARE IN the same run, then sort based on the remainder
        return bRemainder.localeCompare(aRemainder); // Reverse alphabetically
      } else {
        // If a and b ARE NOT IN the same run, then sort based on Run
        return bRun.localeCompare(aRun); // Reverse alphabetically
      }
    };

    if (aProject === projectName) {
      // If a is in the current project

      if (bProject === projectName) {
        // AND b is also in the current project
        return runAndMapSorting(); // Sort based on Run and MapItem

      } else {
        // BUT b IS NOT in the current project
        return -1; // SORT A FIRST

      }
    } else if (bProject === projectName) {
      // If a is not in the current project BUT b IS
      return 1; // SORT B FIRST

    } else {
      // If neither a nr b in the current project
      if (aProject === bProject) {
        // If a and b are in the same project
        return runAndMapSorting(); // Sort based on Run and Map Item
      } else {
        // If a and b are in different projects
        return bProject.localeCompare(aProject); // Sort by project
      }
    }
  }
}
